<template>
  <AnalyticsWidget
    title="Incoming Stock"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :scrollable="true"
    >
    <template v-slot:title>
      <ReportFilterPopover
        class="float-right"
        headerTitle="Incoming Stock Filter"
        :appendToBody="true"
        :showApplyButton="true"
        :showResetButton="true"
        @apply="applyIncomingStockFilter"
        @reset="resetIncomingFilter">
        <template v-slot:popover>
          <ReportFilterGroup label="Incoming Stock Date Range" subTitle="Date range when purchase order is expected to arrive">
            <ReportDateRangeSelect
              :startDate="editingIncomingStockFilter.startDate"
              :endDate="editingIncomingStockFilter.endDate"
              @updateStartDate="editingIncomingStockFilter.startDate = $event"
              @updateEndDate="editingIncomingStockFilter.endDate = $event" />
          </ReportFilterGroup>
        </template>
        <template v-slot:filterDisplay>
          <div>
            <i class="far fa-calendar mr-2"></i>{{appliedIncomingStockFilter.startDate | formatDate}} - {{appliedIncomingStockFilter.endDate | formatDate}}
          </div>
        </template>
      </ReportFilterPopover>
    </template>
    <template v-slot:subtitle>
      List of stock expected to arrive between <strong>{{formatDate(appliedIncomingStockFilter.startDate)}}</strong> and <strong>{{formatDate(appliedIncomingStockFilter.endDate)}}</strong>. Includes overdue purchase orders
    </template>
    <div class="p-2">
      <LoadingPanel :loading="loading" />
      <AnalyticsBreakdownTable v-if="!loading">
      <template v-slot:header>
        <tr>
          <th>Stock</th>
          <th>Item Info</th>
          <th>Purchase Order</th>
          <th>Supplier</th>
          <th>Type</th>
          <th>Status</th>
          <th>Quantity</th>
          <th>Advised Date</th>
          <th>Required Date</th>
        </tr>
      </template>
      <template v-slot:body>
        <tr v-for="orderItem in incomingPurchaseOrderItemList" :key="orderItem.id">
          <td>
            <LinkStock :id="orderItem.stock.id" :name="orderItem.stock.name" />
          </td>
          <td>
            {{formatHyphenEmptyValue(displayItemInfo(orderItem))}}
          </td>
          <td>
            <LinkPurchaseOrder :id="orderItem.purchaseOrder.id" :name="orderItem.purchaseOrder.sequenceNumber" />
          </td>
          <td>
            <LinkContact :id="orderItem.purchaseOrder.contact.id" :name="orderItem.purchaseOrder.contact.name" />
          </td>
          <td>
            <LabelBadge :labelClass="`label-po-type-${orderItem.purchaseOrder.type}`">{{orderItem.purchaseOrder.type | formatLookupName(PurchaseOrderTypeLookup)}}</LabelBadge>
          </td>
          <td>
            <LabelBadge :labelClass="`label-po-status-${orderItem.purchaseOrder.status}`">{{orderItem.purchaseOrder.status | formatLookupName(PurchaseOrderStatusTypeLookup)}}</LabelBadge>
          </td>
          <td>{{orderItem.quantity | formatDecimal(2, true)}}</td>
          <td>
            <span :class="{'financial-loss': isDateOverdue(orderItem.purchaseOrder.dateAdvised)}">{{orderItem.purchaseOrder.dateAdvised | formatDate}}</span>
            <span v-if="!orderItem.purchaseOrder.dateAdvised">-</span>
          </td>
          <td>
            <span :class="{'financial-loss': isDateOverdue(orderItem.purchaseOrder.dateRequired)}">{{orderItem.purchaseOrder.dateRequired | formatDate}}</span>
          </td>                    
        </tr>
        <tr class="static-datatable-row-nostripe table-totals-row">
          <td colspan="5" class="font-weight-bold text-right border-top">Total:</td>
          <td class="font-weight-bold border-top">{{totalIncomingQuantity}}</td>
          <td class="border-top" colspan="2">&nbsp;</td>
        </tr>
      </template>
    </AnalyticsBreakdownTable>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsBreakdownTable from '@/modules/intelligence/components/analyticsBreakdownTable'
import AnalyticsBreakdownValueCell from '@/modules/intelligence/components/analyticsBreakdownValueCell'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'

import { InventoryStockTypes, PurchaseOrderStatusTypeLookup, PurchaseOrderTypeLookup } from '@/store/types'

export default {
  name: 'PurchasingIncomingStockWidget',
  components: {
    AnalyticsWidget,
    AnalyticsBreakdownTable,
    AnalyticsBreakdownValueCell,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup
  },
  props: {
    stockId: Number,
    filter: Object,
    gridWidth: Number,
    gridHeight: Number,
    startDate: String,
    endDate: String,
  },
  computed: {
    totalIncomingQuantity() {
      return this.incomingPurchaseOrderItemList.reduce((curQty, item) => curQty + item.quantity, 0)
    },
    subTitle() {
      return `List of stock expected to arrive between ${this.formatDate(this.appliedIncomingStockFilter.startDate)} and ${this.formatDate(this.appliedIncomingStockFilter.endDate)}. Includes overdue purchase orders`
    },
    hasStockFilter() {
      return !!this.stockId
    }
  },
  data() {
    return {
      loading: true,
      incomingPurchaseOrderItemList: [],

      defaultIncomingStockFilter: {
        startDate: this.startDate || moment().local().startOf('month').startOf('day').toDate().toISOString(),
        endDate: this.endDate || moment().local().endOf('month').endOf('day').toDate().toISOString(),
      },
      editingIncomingStockFilter: {},
      appliedIncomingStockFilter: {},
    }
  },
  watch: {
    stockId: function() {
      this.requestData()
    },
    filter: function() {
      this.requestData()
    },
    startDate: function() {
      this.editingIncomingStockFilter.startDate = this.startDate
      this.appliedIncomingStockFilter.startDate = this.startDate
    },
    endDate: function() {
      this.editingIncomingStockFilter.endDate = this.endDate
      this.appliedIncomingStockFilter.endDate = this.endDate
    }
  },
  created() {
    this.PurchaseOrderStatusTypeLookup = PurchaseOrderStatusTypeLookup
    this.PurchaseOrderTypeLookup = PurchaseOrderTypeLookup
  },
  mounted() {
    this.loading = true
    this.editingIncomingStockFilter = _.clone(this.defaultIncomingStockFilter)
    this.appliedIncomingStockFilter = _.clone(this.defaultIncomingStockFilter)

    this.requestData()    
  },
  methods: {
    requestData() {
      var params = {
        startDate: this.appliedIncomingStockFilter.startDate,
        endDate: this.appliedIncomingStockFilter.endDate,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null,
        stockId: this.stockId
      }

      this.$apiService.analyticsService.getPurchasingIncomingStockList(params)
      .then(response => {
        this.incomingPurchaseOrderItemList = response.data
      })
      .finally(() => {
        this.loading = false
      })
    },
    applyIncomingStockFilter() {
      this.appliedIncomingStockFilter = _.clone(this.editingIncomingStockFilter)

      this.requestData()
    },
    resetIncomingFilter() {
      this.editingIncomingStockFilter = _.clone(this.defaultIncomingStockFilter)
      this.appliedIncomingStockFilter = _.clone(this.defaultIncomingStockFilter)

      this.requestData()
    },
    isDateOverdue(date) {
      if(date == null)
        return false
      return moment(date).isBefore(moment())
    },
    displayItemInfo(purchaseOrderItem) {
      if(purchaseOrderItem.stockType == InventoryStockTypes.glass) {
        return `${purchaseOrderItem.glassHeight} x ${purchaseOrderItem.glassWidth}`
      }

      return ''
    }
  }
}
</script>